import { ComponentProps } from 'react';
import { css } from '@emotion/react';
import { cx } from '@linaria/atomic';
import { useTranslation } from '@frontend/i18n';
import { useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  Button,
  PopoverMenu,
  PopoverMenuItem,
  StepperHorizontalRaw,
  StepperStyles,
  Text,
  usePopoverMenu,
} from '@frontend/design-system';

type Props = {
  currentStep: number;
  onStepClick?: (step: number) => void;
  className?: string;
  onContinue?: () => void;
  continueLoading?: boolean;
  continueDisabled?: boolean;
  onApplyChangesToTemplate?: () => void;
  onSaveAsNewTemplate?: () => void;
  onSkip?: () => void;
  onSend?: () => void;
  sendLoading?: boolean;
  sendDisabled?: boolean;
};
type Status = ComponentProps<typeof StepperHorizontalRaw>['steps'][number]['status'];
export const FaxCreationStepper = ({
  className,
  currentStep,
  onStepClick,
  onContinue,
  continueLoading,
  continueDisabled,
  onApplyChangesToTemplate,
  onSaveAsNewTemplate,
  onSkip,
  onSend,
  sendLoading,
  sendDisabled,
}: Props) => {
  const { t } = useTranslation('fax');
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const isMedium = useMatchMedia({ maxWidth: breakpoints.medium.max });
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const hasContinueMenu = !!(onApplyChangesToTemplate || onSaveAsNewTemplate);
  const { getTriggerProps, getMenuProps, close, isOpen } = usePopoverMenu<HTMLButtonElement>({
    placement: 'bottom-end',
  });

  const getStatus = (step: number): Status => {
    return currentStep < step ? 'inactive' : currentStep === step ? 'currActive' : 'completed';
  };

  return (
    <div
      className={cx('stepper', className, StepperStyles.stepper(true, false))}
      style={StepperStyles.stepperVars(4)}
      css={css`
        display: flex;
        align-items: center;
        overflow: visible;
        width: 100%;
        max-width: 100%;
        margin: 0;
        padding: 0 ${theme.spacing(1)};
        box-sizing: border-box;
      `}
    >
      {/* Left: Back button — always reserves space so stepper stays centered */}
      <div css={{ display: 'flex', alignItems: 'center', flexShrink: 0, minWidth: 80 }}>
        {onStepClick && (
          <Button size='small' variant='tertiary' iconName='back' onClick={() => onStepClick?.(currentStep - 2)}>
            {t('Back')}
          </Button>
        )}
      </div>

      {/* Center: Stepper dots */}
      <div css={{ flex: 1, minWidth: 0, marginTop: theme.spacing(1.5) }}>
        <StepperHorizontalRaw
          steps={[
            { status: getStatus(1), title: isMobile ? '' : t('New Fax') },
            { status: getStatus(2), title: isMobile ? '' : t('Cover Sheet') },
            { status: getStatus(3), title: isMobile ? '' : isMedium ? t('Upload Pages') : t('Add Documents') },
            { status: getStatus(4), title: isMobile ? '' : t('Finalize') },
          ]}
          onStepClick={(step) => onStepClick?.(step)}
          css={{ margin: 0 }}
        />
      </div>

      {/* Right: Send / Continue buttons */}
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          minWidth: 80,
          justifyContent: 'flex-end',
          paddingRight: theme.spacing(1),
        }}
      >
        {onSkip && (
          <>
            <Button size='small' variant='secondary' onClick={onSkip} css={{ marginRight: theme.spacing(1) }}>
              {t('Skip')}
            </Button>
          </>
        )}
        {onSend && (
          <Button
            size='small'
            variant='primary'
            iconName='send'
            onClick={onSend}
            loading={sendLoading}
            disabled={sendDisabled}
          >
            {isXSmall ? null : t('Send Fax')}
          </Button>
        )}
        {(onContinue || hasContinueMenu) && (
          <div css={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            {onContinue && (
              <Button
                size='small'
                variant='primary'
                onClick={onContinue}
                loading={continueLoading}
                disabled={continueDisabled}
                css={{
                  borderTopRightRadius: hasContinueMenu ? 0 : undefined,
                  borderBottomRightRadius: hasContinueMenu ? 0 : undefined,
                }}
              >
                {t('Continue')}
              </Button>
            )}
            {hasContinueMenu && (
              <>
                <Button
                  size='small'
                  variant='primary'
                  iconName={isOpen ? 'caret-up' : 'caret-down'}
                  disabled={continueLoading || continueDisabled}
                  css={{
                    borderTopLeftRadius: onContinue ? 0 : undefined,
                    borderBottomLeftRadius: onContinue ? 0 : undefined,
                    borderLeft: onContinue ? `1px solid rgba(255,255,255,0.3)` : undefined,
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1),
                  }}
                  {...getTriggerProps()}
                />
                <PopoverMenu {...getMenuProps()}>
                  <div
                    css={{
                      padding: theme.spacing(1, 2),
                      pointerEvents: 'none',
                    }}
                  >
                    <Text weight='bold'>{t('Continue and...')}</Text>
                  </div>
                  {onApplyChangesToTemplate && (
                    <PopoverMenuItem
                      onClick={() => {
                        close();
                        onApplyChangesToTemplate();
                      }}
                    >
                      {t('Apply changes to this template')}
                    </PopoverMenuItem>
                  )}
                  {onSaveAsNewTemplate && (
                    <PopoverMenuItem
                      onClick={() => {
                        close();
                        onSaveAsNewTemplate();
                      }}
                    >
                      {t('Save as new template')}
                    </PopoverMenuItem>
                  )}
                </PopoverMenu>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
